/**
 * Type definitions for RampCanvasV2 component
 * Includes shape types, metadata interfaces, and size categories
 */

import { v4 as uuidv4 } from "uuid";
import { Ramp } from "../../../../../types";
import { OrientationType } from "../../../../../utils/spots";
import { SHAPE_COLORS } from "./ShapeEditDialog/constants";

// ============================================================================
// Component Props
// ============================================================================

export type Props = {
  ramp: Ramp;
  setRamp: (ramp: Ramp) => void;
  readOnly?: boolean;
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
  /** Shape ID to center/scroll to. Pass null after centering is complete. */
  centerOnShapeId?: string | null;
  /** Callback when centering is complete */
  onCenterComplete?: () => void;
};

// ============================================================================
// Canvas State Types
// ============================================================================

export type ContextMenu = {
  x: number;
  y: number;
  shapeId: string;
};

export type DraggingPoint = {
  shapeId: string;
  pointIndex: number;
};

export type DraggingShape = {
  shapeId: string;
  offset: Point;
};

export type Point = { x: number; y: number };

export type ReferencePoint = {
  name: "north" | "south" | "east" | "west" | "center";
  validOrientations: OrientationType[];
};

// ============================================================================
// Size Categories
// ============================================================================

/** Aircraft size categories based on typical wingspan */
export type SizeCategory =
  | "very-small" // Max 40ft wingspan
  | "small" // Max 60ft wingspan
  | "midsize" // Max 80ft wingspan
  | "super-midsize" // Max 100ft wingspan
  | "large"; // Max 120ft wingspan

/** Size category UI options - maxFeet = max allowable wingspan */
export const SIZE_CATEGORY_OPTIONS = [
  {
    value: "very-small" as SizeCategory,
    label: "Very Small (≤40ft)",
    maxFeet: 40,
  },
  { value: "small" as SizeCategory, label: "Small (≤60ft)", maxFeet: 60 },
  { value: "midsize" as SizeCategory, label: "Midsize (≤80ft)", maxFeet: 80 },
  {
    value: "super-midsize" as SizeCategory,
    label: "S-Mid (≤100ft)",
    maxFeet: 100,
  },
  { value: "large" as SizeCategory, label: "Large (≤120ft)", maxFeet: 120 },
];

/**
 * Map spot size in feet to size category
 * Uses maxFeet from SIZE_CATEGORY_OPTIONS
 */
export function getDefaultSizeCategory(spotFeet: number): SizeCategory {
  for (const option of SIZE_CATEGORY_OPTIONS) {
    if (spotFeet <= option.maxFeet) {
      return option.value;
    }
  }
  return "large"; // Fallback for > 120ft
}

/**
 * Get max allowed sizes for a spot based on its own size
 * RULE: Spots can only allow aircraft sizes <= their own size
 */
export function getMaxAllowedSizes(spotSize: SizeCategory): SizeCategory[] {
  const sizeHierarchy: SizeCategory[] = [
    "very-small",
    "small",
    "midsize",
    "super-midsize",
    "large",
  ];
  const maxIndex = sizeHierarchy.indexOf(spotSize);
  return sizeHierarchy.slice(0, maxIndex + 1);
}

// ============================================================================
// Metadata Types
// ============================================================================

/**
 * Zone metadata - stored in shape.metadata for Zone shapes
 *
 * Note: Zone name is stored in shape.text
 * Note: Zone priority is stored in shape.priority (0-10, higher = more preferred)
 */
export type ZoneMetadata = {
  /**
   * Disabled flag for zone
   * When true, all spots in this zone are unavailable to parking algorithm
   * Use for closing entire zones during maintenance/construction
   * Default: false (enabled)
   */
  disabled?: boolean;

  /**
   * Staging zone flag
   * When true, this zone is designated for temporary staging of aircraft
   * Used in staging workflow where aircraft arrive at staging spot before moving to permanent spot
   * This flag is propagated to all spots within the zone
   * Default: false
   */
  is_staging?: boolean;
};

/**
 * Egress option representing one possible exit path for a spot
 * Spots can have multiple egress lanes - aircraft chooses best available at departure
 */
export type EgressOption = {
  /** Shape ID of the egress lane */
  laneId: string;
  /** Display name (e.g., "Lane 2") */
  laneName: string;
  /** Spots blocked if this lane is used */
  blocksSpotIds: string[];
};

/**
 * Spot metadata - stored in shape.metadata for Spot shapes
 * Contains temporal parking algorithm configuration
 */
export type SpotMetadata = {
  /**
   * Aircraft size categories allowed to park in this spot
   * RULE: Can only include sizes <= spot's own size category
   * Default: [spot's own size] (e.g., 60ft spot defaults to ["small"])
   */
  allowedSizes?: SizeCategory[];

  /**
   * Egress lane shape IDs this spot can use for departure
   * References Line shapes tagged with "egress_lane"
   * Array because some spots have multiple exit options
   */
  egressLanes?: string[];

  /**
   * Per-lane egress options with blocking relationships
   * Auto-calculated from egressLanes - shows which spots are blocked by each egress option
   * Aircraft will choose the lane with fewest blocks at departure time
   */
  egressOptions?: EgressOption[];

  /**
   * Zone ID this spot belongs to
   * Auto-assigned based on which zone polygon contains the spot's center point
   */
  zoneId?: string;

  /**
   * Gap spot flag (Phase 2a)
   * Marks this spot as a gap spot - smaller spot that overlaps larger spots spatially
   * When true, spot becomes available only when surrounding larger spots are occupied
   */
  isGapSpot?: boolean;

  /**
   * Required occupied spots for gap spot (Phase 2c)
   * Spot IDs that must be occupied for this gap spot to be available
   * Auto-detected based on spatial overlap with larger spots
   * Only applies when isGapSpot is true
   */
  requiresOccupiedSpots?: string[];

  /**
   * Maximum dwell time in minutes for this spot
   * Used for overflow/emergency spots that should only hold aircraft temporarily
   * undefined = no limit (default for normal spots)
   * Examples: 60 = 1 hour, 180 = 3 hours, 1440 = 1 day
   */
  max_dwell_minutes?: number;

  /**
   * Minimum dwell time in minutes for this spot
   * Used for remote/overflow spots to reserve them for long-stay aircraft
   * Aircraft staying >= min_dwell_minutes get priority placement (bonus)
   * Aircraft staying < min_dwell_minutes are discouraged (penalty)
   * undefined = no minimum (default for normal spots)
   * Examples: 480 = 8 hours, 1440 = 1 day, 4320 = 3 days
   */
  min_dwell_minutes?: number;

  /**
   * Disabled flag for spot
   * When true, spot is unavailable to parking algorithm
   * Use for maintenance, construction, or temporarily closing individual spots
   * Default: false (enabled)
   */
  disabled?: boolean;

  /**
   * Staging zone flag (propagated from zone)
   * When true, this spot is in a staging zone for temporary aircraft placement
   * This value is automatically propagated from the zone's is_staging flag
   * Used in staging workflow where aircraft arrive at staging spot before moving to permanent spot
   * Default: false
   */
  is_staging?: boolean;
};

/** Position options for Line text labels */
export type LineTextPosition = "start" | "middle" | "end";

/**
 * Line metadata - stored in shape.metadata for Line shapes
 * Contains text label positioning configuration
 */
export type LineMetadata = {
  /** Position of text label relative to line. Default: 'middle' */
  text_position?: LineTextPosition;
  /** Whether to show text label. Default: true */
  show_text?: boolean;
  /** Staging flag - not used for lines, but included for union type compatibility */
  is_staging?: boolean;
};

// ============================================================================
// Shape Type
// ============================================================================

export type Shape = {
  id: string;
  fbo_id: string;
  ramp_id: string;
  hangar_id?: string;
  tags: string[];
  points: Point[];
  type:
    | "Line"
    | "Text"
    | "Area"
    | "Tie Down T"
    | "Double T"
    | "Circle"
    | "Helipad"
    | "Compass Rose"
    | "Spot"
    | "Zone";
  locked: boolean;
  stroke_style?: string;
  line_style?: "solid" | "dashed";
  line_width?: number;
  fill_style?: string;
  fill_pattern?: string;
  text?: string;
  font_size?: number;
  // this is the height of the image being edited in pixels. we use this to maintain
  // the relative font size
  reference_image_height?: number;

  /**
   * Priority for parking assignments (0-10, higher = preferred)
   * Used by both Spots and Zones
   */
  priority?: number;

  /**
   * Aircraft orientations allowed for reference lines
   * Array of allowed orientations: 'nose_to_line' and/or 'tail_to_line'
   * Only used for shapes tagged with "reference_line"
   */
  orientation?: ("nose_to_line" | "tail_to_line")[];

  /**
   * Reference points for spots - STORED AS TOP-LEVEL COLUMN (not in metadata)
   * WHY: Complex type used by general auto-parking algorithm (not temporal-specific)
   * Contains orientation data and positioning info for runAutoParking()
   */
  reference_points?: ReferencePoint[];

  /**
   * Algorithm-specific metadata stored as JSONB
   * - For Zones: ZoneMetadata (currently empty, uses shape.text for name)
   * - For Spots: SpotMetadata (allowedSizes, egressLanes, blocksSpotIds, zoneId)
   * - For Lines: LineMetadata (text_position, show_text)
   */
  metadata?: ZoneMetadata | SpotMetadata | LineMetadata;

  // V2 Layer Panel fields
  /**
   * User-editable display name for layer panel
   * If not set, auto-generates from type + index (e.g., "Spot 1", "Zone 2")
   */
  display_name?: string;

  /**
   * Visibility toggle for layer panel
   * When false, shape is hidden from canvas but still exists in data
   * Default: true (visible)
   */
  visible?: boolean;

  /**
   * Layer group ID for grouping shapes in layer panel
   * null or undefined = ungrouped
   */
  group_id?: string | null;
};

// ============================================================================
// Factory Functions
// ============================================================================

export const makeDefaultShape = (fbo_id: string, ramp_id: string): Shape => ({
  id: uuidv4(),
  ramp_id,
  fbo_id,
  tags: [],
  points: [],
  type: "Line",
  locked: false,
  line_style: "solid",
  line_width: 1,
  stroke_style: SHAPE_COLORS[0],
  fill_style: "",
  fill_pattern: "",
  font_size: 24,
  text: "",
  priority: 5,
});
